import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { getBranchStorage } from "../../apis/user.api";
import { formatBytes, calculateStoragePercentage, getStorageLimit } from "../../core/utils/storage";
import { useStorageRefresh } from "../../core/utils/storageContext";
import { HardDrive, AlertTriangle, CheckCircle2 } from "lucide-react";

interface StorageData {
  used_storage: number;
  storage_limit: number;
  used_percentage: number;
}

const BranchStorageDisplayCompact = () => {
  const user = useSelector((state: any) => state.userSlice.user);
  const [storageData, setStorageData] = useState<StorageData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { storageKey, pendingStorageChange } = useStorageRefresh();

  useEffect(() => {
    if (user?.branch_id) {
      fetchStorageData();
    }
  }, [user?.branch_id]);

  // Immediate refetch when storageKey changes (after upload/delete)
  useEffect(() => {
    if (user?.branch_id && storageKey > 0) {
      console.log("Storage key changed (compact), immediately refetching");
      // Immediate fetch
      fetchStorageData();
      
      // Then refetch with delays to ensure we get the latest data
      const timer1 = setTimeout(() => {
        fetchStorageData();
      }, 500);
      
      const timer2 = setTimeout(() => {
        fetchStorageData();
      }, 1500);
      
      const timer3 = setTimeout(() => {
        fetchStorageData();
      }, 3000);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [storageKey]);

  const fetchStorageData = async () => {
    if (!user?.branch_id) return;

    setIsLoading(true);
    try {
      const response = await getBranchStorage(user.branch_id);
      console.log("Storage API response (compact):", response);
      
      if (response.status === "success" && response.data) {
        const newStorageData = {
          used_storage: response.data.used_storage || 0,
          storage_limit: response.data.storage_limit || getStorageLimit(),
          used_percentage: response.data.used_percentage || 0
        };
        console.log("Setting storage data (compact):", newStorageData);
        setStorageData(newStorageData);
      } else {
        console.warn("Storage API returned unexpected format (compact):", response);
        setStorageData({
          used_storage: 0,
          storage_limit: getStorageLimit(),
          used_percentage: 0
        });
      }
    } catch (err: any) {
      console.error("Failed to fetch storage data (compact):", err);
      setStorageData({
        used_storage: 0,
        storage_limit: getStorageLimit(),
        used_percentage: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user?.branch_id) {
    return null;
  }

  const used = (storageData?.used_storage || 0) + (pendingStorageChange || 0);
  const limit = storageData?.storage_limit || getStorageLimit();
  const percentage = calculateStoragePercentage(used, limit);
  const available = limit - used;
  const isNearLimit = percentage >= 80;
  const isOverLimit = percentage >= 100;

  // Format storage values
  const usedGB = parseFloat((used / (1024 * 1024 * 1024)).toFixed(2));
  const limitGB = parseFloat((limit / (1024 * 1024 * 1024)).toFixed(0));

  // Simple color scheme
  const getColor = () => {
    if (isOverLimit) return '#ef4444';
    if (isNearLimit) return '#f59e0b';
    return '#10b981';
  };

  const color = getColor();

  return (
    <div 
      className="branch-storage-compact mb-3"
      style={{
        background: '#ffffff',
        borderRadius: '12px',
        padding: '16px',
        border: `1px solid #e5e7eb`,
        position: 'relative'
      }}
    >
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <HardDrive size={18} style={{ color: color }} />
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
            Storage
          </span>
        </div>
        <span style={{ fontSize: '14px', fontWeight: 600, color: color }}>
          {percentage.toFixed(1)}%
        </span>
      </div>

      {/* Progress Bar */}
      <div 
        style={{
          height: '6px',
          borderRadius: '10px',
          background: '#f3f4f6',
          overflow: 'hidden',
          marginBottom: '12px'
        }}
      >
        <div
          role="progressbar"
          style={{
            width: `${Math.min(100, percentage)}%`,
            height: '100%',
            background: color,
            borderRadius: '10px',
            transition: 'width 0.3s ease'
          }}
        />
      </div>

      {/* Storage Info */}
      <div className="d-flex justify-content-between align-items-center">
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          {usedGB} GB used
        </span>
        <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>
          {limitGB} GB total
        </span>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div 
            style={{
              width: '20px',
              height: '20px',
              border: `2px solid #f3f4f6`,
              borderTop: `2px solid ${color}`,
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BranchStorageDisplayCompact;
